import { expandFontTheme, theme } from '@contrast-security-inc/design-system-foundations';
import { css } from '@emotion/css';
export const validLabelFonts = ['body', 'label'];
export const validLabelColors = ['neutral60', 'neutral100'];
export const fnOrVal = (val, fn, ...args) => (fn ? fn(...args) : val);
export const escapeString = (string) => {
    return string === null ? '' : string.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
};
export const inputContainerStyles = css `
  display: flex;
  flex-direction: column;
  padding-top: 10px;
  outline: unset;
`;
export const inputWrapperStyles = (hasLabel) => css `
  align-items: center;
  background-color: ${theme.color.white};
  border-radius: 4px;
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  position: relative;

  fieldset {
    border-radius: 4px;
    border: 1px solid ${theme.color.neutral60};
    position: absolute;
    inset: ${hasLabel ? '-10px' : '0px'} 0px 0px;
    margin: 0px;
    padding: 0px 8px;
    pointer-events: none;
  }

  &:hover {
    fieldset {
      border-color: ${theme.color.neutral100};
    }

    [data-clear-input] {
      display: flex;
    }
  }

  &:focus-within {
    fieldset {
      border-color: ${theme.color.turquoise70};
    }

    [data-clear-input] {
      display: flex;
    }
  }

  label {
    ${expandFontTheme(theme.font.metadata)}
    color: ${theme.color.neutral100};
    display: flex;
    flex-direction: row;
    left: 12px;
    padding-left: 4px;
    padding-right: 4px;
    position: absolute;
    white-space: nowrap;
    overflow: hidden;
    top: -10px;
    transition-delay: 0ms, 0ms, 0ms, 0ms;
    transition-duration: 200ms, 200ms, 200ms, 200ms;
    transition-property: font, left, top, color;
    /* stylelint-disable-next-line declaration-block-no-redundant-longhand-properties */
    transition-timing-function:
      cubic-bezier(0, 0, 0.2, 1),
      cubic-bezier(0, 0, 0.2, 1),
      cubic-bezier(0, 0, 0.2, 1),
      cubic-bezier(0, 0, 0.2, 1);
    z-index: 1;
  }

  input,
  textarea {
    ${expandFontTheme(theme.font.bodyStrong)}
    border-radius: 4px;
    border: none;
    box-sizing: border-box;
    color: ${theme.color.neutral100};
    display: flex;
    flex-grow: 1;
    outline: none;
    overflow-x: hidden;
    padding: 12px 16px;
  }

  textarea {
    /* line-height + padding */
    min-height: 48px;
  }

  /* stylelint-disable-next-line selector-no-vendor-prefix */
  input::placeholder,
  input::-webkit-input-placeholder,
  textarea::placeholder,
  textarea::-webkit-input-placeholder { /* stylelint-disable-line selector-no-vendor-prefix */
    ${expandFontTheme(theme.font.bodyItalic)}
  }

  input:required + label,
  textarea:required + label {
    &::after {
      color: ${theme.color.red70};
      content: " *";
      display: block;
      padding-left: 4px;
    }
  }

  legend {
    ${expandFontTheme(theme.font.metadata)}
    margin-left: 2px;
    max-width: 100%;
    padding-left: 4px;
    padding-right: 6px;
    position: relative;
    visibility: hidden;
    width: fit-content;
    white-space: nowrap;

    &::before {
      background-color: ${theme.color.white};
      border-radius: 4px;
      bottom: 0;
      content: "";
      height: 100%;
      left: 0;
      opacity: 1;
      position: absolute;
      transform-origin: bottom;
      transition: 200ms all ease;
      visibility: visible;
      width: 100%;
    }
  }

  input:required ~ fieldset > legend,
  textarea:required ~ fieldset > legend {
    &::before {
      width: calc(100% + 8px);
    }
  }

  input:disabled ~ fieldset > legend,
  textarea:disabled ~ fieldset > legend {
    padding-right: 4px;
  }
`;
export const labelAsPlaceholderStyles = css `
  input:placeholder-shown:not(:focus) + label,
  textarea:placeholder-shown:not(:focus) + label {
    ${expandFontTheme(theme.font.body)};
    color: ${theme.color.neutral70};
    display: flex;
    height: 24px;
    inset: 50% 12px;
    margin: auto;
    pointer-events: none;
    position: absolute;

    &::after {
      box-shadow: none;
      transition: all 200ms ease 200ms;
    }
  }

  input:placeholder-shown:not(:focus) ~ fieldset > legend,
  textarea:placeholder-shown:not(:focus) ~ fieldset > legend {
    padding: 0;
    max-width: 0px;
    white-space: nowrap;

    &::before {
      height: 0%;
      opacity: 0;
    }
  }
`;
export const inputTopRowStyles = css `
  align-self: flex-end;
  display: flex;
`;
export const inputErrorStyles = css `
  /* stylelint-disable-next-line no-descending-specificity */
  fieldset {
    border-color: ${theme.color.red50};
  }

  &:hover fieldset {
    border-color: ${theme.color.red50};
  }

  &:focus-within fieldset {
    border-color: ${theme.color.red50};
  }

  /* stylelint-disable-next-line no-descending-specificity */
  label {
    color: ${theme.color.red70};
  }
`;
export const inputDisabledStyles = css `
  background-color: ${theme.color.neutral20};
  cursor: not-allowed;

  /* stylelint-disable-next-line no-descending-specificity */
  fieldset {
    border-color: ${theme.color.neutral40};
  }

  &:hover fieldset {
    border-color: ${theme.color.neutral40};
  }

  &:focus-within fieldset {
    border-color: ${theme.color.neutral40};
  }

  input,
  textarea {
    background-color: ${theme.color.neutral20};
    color: ${theme.color.neutral60};
    cursor: not-allowed;

    &:required {
      &::after {
        color: ${theme.color.neutral60};
      }
    }
  }

  /* stylelint-disable-next-line no-descending-specificity */
  label {
    background-color: ${theme.color.neutral20};
    color: ${theme.color.neutral60};
    cursor: not-allowed;
    left: 11px;
  }

  /* stylelint-disable-next-line no-descending-specificity */
  input:required + label,
  textarea:required + label {
    /* stylelint-disable-next-line no-descending-specificity */
    &::after {
      color: ${theme.color.neutral60};
    }
  }
`;
// `allowTypeAhead` will eventually be a prop on the parent component
export const selectedItemsWrapperStyles = (isRegularMultiSelect, inputHeight, allowTypeAhead, isDisabled) => {
    const enabledCursor = allowTypeAhead ? 'text' : 'pointer';
    return css `
    align-content: ${isRegularMultiSelect ? 'flex-start' : 'center'};
    align-items: center;
    cursor: ${isDisabled ? 'not-allowed' : enabledCursor};
    display: flex;
    flex: 1;
    height: ${inputHeight ? `${inputHeight}px` : 'auto'};
    overflow: hidden;
    ${isRegularMultiSelect
        ? `box-sizing: border-box;
      flex-flow: wrap;
      gap: 8px 4px;
      max-height: 400px;
      overflow-y: auto;
      padding: 12px;`
        : ''}
  `;
};
export const selectedItemTextStyles = (hasClearButton) => css `
  align-items: center;
  display: flex;
  flex-flow: wrap;
  height: 100%;
  width: 100%;
  top: 0;
  box-sizing: border-box;
  max-width: calc(100% - ${hasClearButton ? '70px' : '38px'}); /** save room for clear and the expand/collapse button */
  color: ${theme.color.neutral100};
  overflow: hidden;
  position: absolute;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 12px 0px 12px 16px;
  ${expandFontTheme(theme.font.bodyStrong)};

  &:focus {
    outline: none;
  }

  > .selected-item-text-textoverflow-wrapper {
    text-align: left;
    width: 100%;
  }
`;
export const selectInputContainerStyles = css `
  box-sizing: border-box;
  position: relative;
`;
export const selectInputWrapperOpenMenuStyles = (isOpen) => {
    if (isOpen) {
        return css `
      /* stylelint-disable-next-line no-descending-specificity */
      & fieldset,
      &:hover fieldset {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        border-color: ${theme.color.turquoise70};
      }
    `;
    }
};
export const selectInputWrapperStyles = (isRegularMultiSelect) => css `
  box-sizing: border-box;
  z-index: 1001; /* Needs to be below Listbox for case of stacked selects */

  /* stylelint-disable-next-line no-descending-specificity */
  input,
  textarea {
    min-width: 20px;
    padding-right: 0px; /* use the button padding */
    ${isRegularMultiSelect ? 'padding: 0;' : ''}
  }
`;
export const buttonResetStyles = css `
  background: none;
  border: none;
  cursor: pointer;
  margin: 0;
  padding: 0;
`;
